"use client";

import { Mail, MapPin, Phone, Briefcase, Users, AlertCircle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function GlobalPresence() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridRefVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const channels = [{
    label: "// GENERAL INQUIRIES",
    icon: Mail,
    title: "Editorial Desk",
    email: "studio@thorne.com",
    phone: "+44 20 7946 0123",
    availability: "M-F · 09:00-19:00 GMT"
  }, {
    label: "// PARTNERSHIPS & PRESS",
    icon: Briefcase,
    title: "Strategic Alliances",
    email: "press@thorne.com",
    phone: "+44 20 7946 0124",
    availability: "M-F · 10:00-18:00 GMT"
  }, {
    label: "// CLIENT SUPPORT",
    icon: AlertCircle,
    title: "Atelier Concierge",
    email: "bespoke@thorne.com",
    phone: "+44 20 7946 0125",
    availability: "24/7 · Priority Channel"
  }];
  const offices = [{
    city: "LONDON",
    coords: "51.5074° N, 0.1278° W",
    address: "12 Shoreditch High St, London"
  }, {
    city: "PARIS",
    coords: "48.8566° N, 2.3522° E",
    address: "44 Rue de Turenne, Le Marais"
  }, {
    city: "TOKYO",
    coords: "35.6762° N, 139.6503° E",
    address: "5-17-1 Jingumae, Shibuya"
  }];
  return <section data-section-id="3399" id="global-presence" className="relative bg-background text-foreground overflow-hidden pt-40 pb-24 md:pt-[180px] md:pb-28 lg:pt-[220px] lg:pb-[140px]">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: "linear-gradient(to right, hsl(25 25% 12%) 1px, transparent 1px)",
      backgroundSize: "80px 100%"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-lg blur-3xl opacity-20" style={{
      background: "radial-gradient(circle, hsl(190 40% 50% / 0.3) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`max-w-5xl transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-10">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-lg opacity-75 animate-ping" style={{
              backgroundColor: "var(--chart-2)"
            }} />
              <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
              backgroundColor: "var(--chart-2)"
            }} />
            </span>
            <span className="font-mono text-xs tracking-widest uppercase" style={{
            color: "var(--chart-2)"
          }}>
              // CONTACT — ATELIER ACCESS
            </span>
          </div>

          <h1 className="font-serif font-light text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight">
            Connect with the{" "}
            <span className="italic font-normal" style={{
            color: "var(--chart-2)"
          }}>
              studio
            </span>
            .
          </h1>

          <p className="mt-8 max-w-2xl font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Three dedicated channels. Three global ateliers. Every inquiry is routed
            to our master tailors and design leads to ensure the highest standard of 
            technical precision.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-4 gap-x-10 gap-y-14 transition-all duration-700 ease-out delay-150 ${gridRefVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          {channels.map((channel, i) => {
          const Icon = channel.icon;
          return <div key={channel.label} data-index={i} className="border-t border-border pt-8 group">
                <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase mb-8">
                  {channel.label}
                </div>

                <Icon className="h-7 w-7 mb-8 text-foreground/80 stroke-[1.25] transition-colors duration-300 group-hover:text-foreground" aria-hidden="true" />

                <h3 className="font-serif text-2xl md:text-[32px] leading-tight text-foreground mb-8 tracking-tight">
                  {channel.title}
                </h3>

                <div className="space-y-3">
                  <Link to={`mailto:${channel.email}`} className="block font-mono text-sm relative w-fit transition-opacity hover:opacity-80" style={{
                color: "var(--chart-2)"
              }}>
                    <span className="relative inline-block">
                      {channel.email}
                      <span className="absolute left-0 -bottom-0.5 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
                    backgroundColor: "var(--chart-2)"
                  }} />
                    </span>
                  </Link>

                  <Link to={`tel:${channel.phone.replace(/[^\d+]/g, "")}`} className="block font-mono text-sm text-foreground/80 hover:text-foreground transition-colors">
                    {channel.phone}
                  </Link>
                </div>

                <div className="mt-8 flex items-center gap-2">
                  <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "var(--chart-2)"
              }} />
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
                    {channel.availability}
                  </span>
                </div>
              </div>;
        })}

          {}
          <div className="border-t border-border pt-8 lg:border-l lg:border-t lg:pl-8 lg:-ml-2">
            <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase mb-8 flex items-center gap-2">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              // GLOBAL ATELIERS
            </div>

            <div className="space-y-8">
              {offices.map((office, i) => <div key={office.city} data-index={i} className="group/office relative">
                  <div className="flex items-baseline justify-between mb-2">
                    <h4 className="font-default text-sm font-semibold tracking-[0.2em] text-foreground">
                      {office.city}
                    </h4>
                    <Phone className="h-3 w-3 text-muted-foreground opacity-0 group-hover/office:opacity-100 transition-opacity" aria-hidden="true" />
                  </div>

                  <div className="font-mono text-[10px] tracking-wider text-muted-foreground mb-3">
                    {office.coords}
                  </div>

                  <div className="font-default text-sm text-foreground/80 leading-relaxed mb-3">
                    {office.address}
                  </div>

                  <Badge variant="outline" className="font-mono text-[10px] tracking-widest border-border bg-transparent" style={{
                color: "var(--chart-2)",
                borderColor: "hsl(190 40% 50% / 0.4)"
              }}>
                    BY APPOINTMENT ONLY
                  </Badge>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div className="mt-24 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
            <Users className="h-3 w-3" aria-hidden="true" />
            <span>STUDIO THORNE — Technical Couture Architecture</span>
          </div>
          <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
            <span style={{
            color: "var(--chart-2)"
          }}>●</span> ALL ATELIERS OPERATIONAL
          </div>
        </div>
      </div>
    </section>;
}